import { queryOptions } from "@tanstack/react-query";
import { STALE } from "@/lib/api/query-client";
import { keys } from "@/lib/api/query-keys";
import { directoryApi } from "./directory.api";
import type { PeopleFilters } from "./directory.types";

/**
 * Query definitions for the directory.
 *
 * `queryOptions` rather than bespoke hooks, so the key and the fetcher travel together: the
 * list, the drawer that prefetches and the mutation that invalidates all name the same thing.
 *
 * NO COMPANY IN THESE KEYS, and that is not an oversight. `keys.directory.*` says why: the
 * server narrows the directory by the caller's own reach, and the company filter — when the
 * screen offers one — is part of the filter object, exactly like the search term. It narrows
 * a result; it does not scope a cache.
 */

/** The people list. `STALE.record`: a colleague's title changes about as often as their desk. */
export function peopleQuery(filters: PeopleFilters) {
  return queryOptions({
    queryKey: keys.directory.people(filters),
    queryFn: () => directoryApi.people(filters),
    staleTime: STALE.record,
  });
}

/**
 * One person's full card, for the drawer.
 *
 * `staleTime: 0` and `refetchOnMount: "always"` because this is a detail surface: the drawer is
 * opened on one colleague after another, and a cached card from twenty minutes ago would put
 * the previous person's phone number under this person's name for a frame. `null` disables the
 * query outright — a closed drawer must not hold a request open.
 */
export function personQuery(userId: string | null) {
  return queryOptions({
    queryKey: keys.directory.person(userId ?? ""),
    queryFn: () => directoryApi.person(userId as string),
    enabled: userId !== null && userId !== "",
    staleTime: 0,
    refetchOnMount: "always",
    /**
     * A DETAIL SURFACE NEVER INHERITS KEEP-PREVIOUS-DATA. The client default is
     * `placeholderData: (previous) => previous`, which exists so paging a table does not blank
     * it; here it makes the observer answer the SECOND person's query with the FIRST person's
     * card, `isPlaceholderData: true` and `isPending: false`. The drawer's stale-card guard
     * refuses to draw it — correctly — but `isPending` is false, so nothing is drawn at all and
     * the panel sits blank for the whole fetch. An explicit `undefined` is what disables it:
     * query-core only applies the option when it is not literally undefined.
     *
     * The cast is `exactOptionalPropertyTypes`, not sloppiness: the option's declared type has
     * no `undefined` in it, yet the value must be PRESENT and undefined to beat the default,
     * which "omit the property" cannot do.
     */
    placeholderData: undefined as never,
  });
}

/**
 * WHICH OF THESE PEOPLE MAY THE CALLER OPEN IN THE DIRECTORY (ik-duzeltme 19 Ekim 2026, Z1/I1).
 *
 * A zimmet list or an approval history names people; a name becomes a link to the Kişi Rehberi
 * card only for somebody the directory itself would show (`GET /directory/people?userIds=` —
 * the server's own reach, never a guess here). Anybody else stays plain text. One request per
 * page of names, sorted ids in the key so the same set is one cache entry. `allowed` is the
 * caller's `directory.person.read`: without it nothing is linked and nothing is asked.
 */
export function visiblePeopleQuery(userIds: readonly string[], allowed: boolean) {
  const ids = [...new Set(userIds.filter((id) => id !== ""))].sort().slice(0, 100);
  const filters: PeopleFilters = { userIds: ids.join(","), limit: 100 };
  return queryOptions({
    queryKey: keys.directory.people(filters),
    queryFn: () => directoryApi.people(filters),
    enabled: allowed && ids.length > 0,
    staleTime: STALE.record,
    select: (page) => new Set(page.rows.map((row) => row.userId)),
  });
}

/** The caller's own profile, with the `version` every edit has to round-trip. */
export function myProfileQuery() {
  return queryOptions({
    queryKey: keys.directory.me(),
    queryFn: () => directoryApi.me(),
    staleTime: STALE.record,
  });
}

/**
 * The upload-slot catalogue.
 *
 * `enabled` is the caller's `files.file.read` grant. Reference data that changes with a
 * deployment, so it is cached like reference data; when it never arrives the picker falls back
 * to the documented limits rather than refusing to work (`photoConstraintsFrom`).
 */
export function photoPurposesQuery(enabled: boolean) {
  return queryOptions({
    queryKey: ["files", "purposes", "directory.user_profile"] as const,
    queryFn: () => directoryApi.photoPurposes(),
    enabled,
    staleTime: STALE.reference,
    // A catalogue the screen can do without must not spend three attempts and eight seconds
    // failing before the picker becomes usable.
    retry: false,
  });
}

/**
 * Org units of one company, for the directory filter.
 *
 * Two gates, not one: a company must be resolved (the endpoint is company-scoped) AND the
 * caller must hold `organization.org_unit.read`. Without the grant the filter is absent from
 * the toolbar entirely — the client only hides, and a select that answers 403 is worse than no
 * select at all.
 */
export function directoryOrgUnitsQuery(legalEntityId: string | null, allowed: boolean) {
  return queryOptions({
    queryKey: keys.organization.orgUnits(legalEntityId),
    queryFn: () => directoryApi.orgUnits(legalEntityId as string),
    enabled: allowed && legalEntityId !== null,
    staleTime: STALE.record,
  });
}
